import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, ProgressBar, Spinner, Table } from 'react-bootstrap';
import {
  getMySubscription, cancelSubscription, getSubscriptionEvents, downloadSubscriptionInvoice,
  getBillingDetails, saveBillingDetails, money
} from '../plansApi';

// The owner's "My Subscription" view (§18/§19). Current plan, status, billing, free-period
// countdown, enabled features, usage vs limits, payment history, and actions (upgrade / cancel).
const STATUS_VARIANT = {
  ACTIVE: 'success', FREE_ACTIVE: 'info', TRIAL: 'info', PAST_DUE: 'warning',
  PAYMENT_FAILED: 'danger', CANCELLED: 'secondary', EXPIRED: 'secondary', PENDING: 'warning', NONE: 'light'
};

export default function MySubscriptionPanel() {
  const [sub, setSub] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    getMySubscription().then(setSub).catch(() => setError('Unable to load your subscription.'));
    getSubscriptionEvents().then(setEvents).catch(() => {});
  };
  useEffect(load, []);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!sub) return <div className="text-center py-4"><Spinner animation="border" /></div>;

  if (!sub.moduleEnabled) {
    return <Alert variant="info">Subscription plans are not enabled yet — every feature is available to your business.</Alert>;
  }

  const doCancel = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current billing period?')) return;
    setBusy(true); setError('');
    try {
      await cancelSubscription(true, '');
      setNotice('Your subscription will not renew. You keep access until the end of the current period.');
      load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to cancel.');
    } finally { setBusy(false); }
  };

  const quantityFeatures = sub.features.filter((f) => f.valueType === 'QUANTITY');
  const enabledBooleans = sub.features.filter((f) => f.valueType === 'BOOLEAN' && f.enabled);

  return (
    <div>
      {notice && <Alert variant="success" onClose={() => setNotice('')} dismissible>{notice}</Alert>}

      {/* membership_approval_flow.md — a cash / bank-transfer activation the website admin has not
          verified yet. Shown instead of the generic "no subscription" warning below. */}
      {sub.approvalRequest && ['PENDING', 'FINANCE_REVIEW'].includes(sub.approvalRequest.status) && (
        <Alert variant="info">
          Your request to activate <strong>{sub.approvalRequest.planName || 'a plan'}</strong> is
          {sub.approvalRequest.status === 'FINANCE_REVIEW' ? ' being reviewed by our finance team' : ' awaiting website-admin verification'}.
          {sub.approvalRequest.requestedAt ? ` Submitted ${new Date(sub.approvalRequest.requestedAt).toLocaleDateString('en-IN')}.` : ''}
          {sub.approvalRequest.claimedReference ? ` Reference: ${sub.approvalRequest.claimedReference}.` : ''}
          {sub.approvalRequest.financeRecommendation ? ` Finance recommendation: ${sub.approvalRequest.financeRecommendation}.` : ''}
        </Alert>
      )}

      {!sub.hasAccess && !(sub.approvalRequest && ['PENDING', 'FINANCE_REVIEW'].includes(sub.approvalRequest.status)) && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center">
          <span>
            {sub.status === 'EXPIRED' ? 'Your subscription has expired.' : 'You do not have an active subscription.'}
            {' '}Premium features are disabled.
          </span>
          <Button as={Link} to="/pricing" size="sm" variant="primary">View plans</Button>
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-1">
                {sub.planName || 'No plan'}{' '}
                <Badge bg={STATUS_VARIANT[sub.status] || 'secondary'}>{String(sub.status).replace('_', ' ')}</Badge>
              </h5>
              <div className="text-muted small">
                {sub.billingCycle && sub.billingCycle !== 'FREE' ? `${sub.billingCycle.toLowerCase()} billing · ` : ''}
                {sub.price > 0 ? `${money(sub.price, sub.currency)} / ${sub.billingCycle === 'YEARLY' ? 'year' : 'month'}` : 'Free'}
              </div>
            </div>
            <div className="text-end">
              <Button as={Link} to="/pricing" size="sm" variant="outline-primary">Change plan</Button>
            </div>
          </div>

          <Table borderless size="sm" className="mt-3 mb-0">
            <tbody>
              {sub.startDate && <tr><td className="text-muted">Started</td><td>{new Date(sub.startDate).toLocaleDateString('en-IN')}</td></tr>}
              {sub.endDate && (
                <tr>
                  <td className="text-muted">{sub.status === 'FREE_ACTIVE' ? 'Free period ends' : (sub.cancelAtPeriodEnd ? 'Access ends' : 'Renews')}</td>
                  <td>{new Date(sub.endDate).toLocaleDateString('en-IN')}</td>
                </tr>
              )}
              {sub.freeDaysRemaining != null && (
                <tr><td className="text-muted">Days remaining</td><td>{sub.freeDaysRemaining}</td></tr>
              )}
              {sub.scheduledPlanCode && (
                <tr><td className="text-muted">Scheduled change</td><td>→ {sub.scheduledPlanCode} on {new Date(sub.scheduledChangeAt).toLocaleDateString('en-IN')}</td></tr>
              )}
              <tr><td className="text-muted">Auto-renew</td><td>{sub.autoRenew ? 'On' : 'Off'}</td></tr>
            </tbody>
          </Table>

          {sub.status === 'FREE_ACTIVE' && sub.freeDaysRemaining != null && sub.freeDaysRemaining <= 14 && (
            <Alert variant="warning" className="mt-2 mb-0 small">
              Your free period ends soon. <Link to="/pricing">Upgrade to a paid plan</Link> to keep your features.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {quantityFeatures.length > 0 && (
        <Card className="mb-3">
          <Card.Header>Usage</Card.Header>
          <Card.Body>
            {quantityFeatures.map((f) => {
              const unlimited = f.limit === 'UNLIMITED';
              const pct = unlimited ? 0 : Math.min(100, Math.round((f.used / Math.max(1, f.limit)) * 100));
              return (
                <div key={f.featureCode} className="mb-2">
                  <div className="d-flex justify-content-between small">
                    <span>{f.name}</span>
                    <span>{f.used} / {unlimited ? '∞' : f.limit}</span>
                  </div>
                  {!unlimited && <ProgressBar now={pct} variant={pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success'} />}
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header>Included features</Card.Header>
        <Card.Body>
          {enabledBooleans.length === 0
            ? <span className="text-muted small">Core features only.</span>
            : <div className="d-flex flex-wrap gap-1">
                {enabledBooleans.map((f) => <Badge key={f.featureCode} bg="light" text="dark" className="border">{f.name}</Badge>)}
              </div>}
        </Card.Body>
      </Card>

      {sub.paymentHistory?.length > 0 && (
        <Card className="mb-3">
          <Card.Header>Payment history</Card.Header>
          <Table size="sm" className="mb-0">
            <thead><tr><th>Date</th><th>Amount</th><th>Cycle</th><th>Reference</th><th>Status</th><th>Invoice</th></tr></thead>
            <tbody>
              {sub.paymentHistory.map((p, i) => (
                <tr key={i}>
                  <td>{p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td>{money(p.amount, p.currency)}</td>
                  <td>{(p.billingCycle || '').toLowerCase()}</td>
                  <td className="small">{p.reference || '—'}</td>
                  <td>{p.status}</td>
                  <td>
                    {p.invoiceId
                      ? <Button size="sm" variant="link" className="p-0" onClick={() => downloadSubscriptionInvoice(p.invoiceId, p.reference)}>Download</Button>
                      : <span className="text-muted small">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <BillingDetailsCard />

      {sub.hasAccess && sub.activationType !== 'GRANDFATHERED' && !sub.cancelAtPeriodEnd && (
        <Button variant="outline-danger" size="sm" disabled={busy} onClick={doCancel}>Cancel subscription</Button>
      )}

      {events.length > 0 && (
        <details className="mt-3">
          <summary className="small text-muted">History</summary>
          <ul className="small text-muted mt-2">
            {events.slice(0, 15).map((e) => (
              <li key={e.id}>{new Date(e.at).toLocaleString('en-IN')} — {e.action.replace(/_/g, ' ')}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// manual_vs_automatic_invoicing.md - the GSTIN + legal name printed on the platform's membership
// tax invoice to this owner. Optional; blank just prints no GSTIN row.
function BillingDetailsCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { getBillingDetails().then(setForm).catch(() => setForm({ billingName: '', gstin: '' })); }, []);
  if (!form) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaved(false);
    try { await saveBillingDetails(form); setSaved(true); } finally { setSaving(false); }
  };

  return (
    <Card className="mb-3">
      <Card.Header>Billing details for invoices</Card.Header>
      <Card.Body>
        <p className="text-muted small">Printed on the tax invoice we raise to you for each membership payment.</p>
        {saved && <Alert variant="success" className="py-1 small" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
        <Form onSubmit={submit}>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Label className="small">Registered / legal name</Form.Label>
              <Form.Control size="sm" value={form.billingName}
                onChange={(e) => setForm({ ...form, billingName: e.target.value })} placeholder="As on your GST certificate" />
            </div>
            <div className="col-md-4">
              <Form.Label className="small">GSTIN (optional)</Form.Label>
              <Form.Control size="sm" value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
